import type {
  ProductEditorForm,
  ProductEditorSyncValidator,
  ProductEditorAsyncValidator,
} from '@/components/forms/ProductForm.types'
import type { ProductRole } from '@/lib/product-role'
import type {
  ProductStorefrontVisibility as StorefrontVisibility,
  ProductAddOnStorefrontVisibility as AddOnVisibility,
} from '@/lib/services/products/products.types'
import type { DetailCardAccent } from '@/components/patterns/detail-page'
import type { ComponentProps, ReactNode } from 'react'
import type { DropdownMenuItem as DropdownMenuItemType } from '@/components/ui/dropdown-menu'
import type { LucideIcon } from 'lucide-react'
import { useStore } from '@tanstack/react-form'
import { Check, Eye, EyeOff, FolderTree } from 'lucide-react'
import { useIsReadOnly } from '@/lib/casl'
import { ProductAddOnStorefrontVisibility } from '@/lib/enums/ProductAddOnStorefrontVisibility'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { isAddOnProductRole } from '@/lib/services/products/products.form'
import { enumToSelectOptions } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'

const storefrontVisibilityOptionIcons: Record<string, LucideIcon | undefined> = {
  [ProductStorefrontVisibility.Always.Value]: Eye,
  [ProductStorefrontVisibility.PublishedCollection.Value]: FolderTree,
  [ProductStorefrontVisibility.Hidden.Value]: EyeOff,
}

const storefrontVisibilityOptions = enumToSelectOptions(ProductStorefrontVisibility).map(
  (option) => {
    const Icon = storefrontVisibilityOptionIcons[option.id]

    return {
      ...option,
      start: Icon ? <Icon /> : null,
    }
  }
)

const addOnStorefrontVisibilityOptionIcons: Record<string, LucideIcon | undefined> = {
  [ProductAddOnStorefrontVisibility.Always.Value]: Eye,
  [ProductAddOnStorefrontVisibility.Hidden.Value]: EyeOff,
}

const addOnStorefrontVisibilityOptions = enumToSelectOptions(ProductAddOnStorefrontVisibility).map(
  (option) => {
    const Icon = addOnStorefrontVisibilityOptionIcons[option.id]

    return {
      ...option,
      start: Icon ? <Icon /> : null,
    }
  }
)

function getProductStorefrontVisibilityMeta(
  visibility: StorefrontVisibility | null | undefined,
  { hasVisibleCollection = false } = {}
): { accent: DetailCardAccent; warning: boolean } {
  switch (visibility) {
    case ProductStorefrontVisibility.Always.Value:
      return {
        accent: 'mint',
        warning: false,
      }
    case ProductStorefrontVisibility.PublishedCollection.Value:
      return {
        accent: hasVisibleCollection ? 'periwinkle' : 'warning',
        warning: !hasVisibleCollection,
      }
    case ProductStorefrontVisibility.Hidden.Value:
    case null:
    case undefined:
    default:
      return {
        accent: 'warning',
        warning: true,
      }
  }
}

export function getProductSummaryVisibilityMeta({
  addOnVisibility,
  hasVisibleCollection = false,
  isAddOnProduct = false,
  isSoldStandalone = true,
  isSubscriptionPackage = false,
  storefrontVisibility,
}: {
  addOnVisibility?: AddOnVisibility | null
  hasVisibleCollection?: boolean
  isAddOnProduct?: boolean
  isSoldStandalone?: boolean
  isSubscriptionPackage?: boolean
  storefrontVisibility?: StorefrontVisibility | null
}): { accent: DetailCardAccent; warning: boolean } {
  if (isSubscriptionPackage) {
    return storefrontVisibility === ProductStorefrontVisibility.Hidden.Value
      ? {
          accent: 'warning',
          warning: true,
        }
      : {
          accent: 'mint',
          warning: false,
        }
  }

  if (isAddOnProduct && !isSoldStandalone) {
    return addOnVisibility === ProductAddOnStorefrontVisibility.Always.Value
      ? {
          accent: 'mint',
          warning: false,
        }
      : {
          accent: 'warning',
          warning: true,
        }
  }

  return getProductStorefrontVisibilityMeta(storefrontVisibility, {
    hasVisibleCollection,
  })
}

function getOptionLabel(options: readonly { id: string; name: string }[], value: string): string {
  return options.find((option) => option.id === value)?.name ?? value
}

function getCombinedVisibilityLabel(
  options: readonly { id: string; name: string }[],
  value: string
): string {
  if (value === ProductStorefrontVisibility.PublishedCollection.Value) {
    return 'In Visible Collections'
  }

  return getOptionLabel(options, value)
}

function ProductVisibilityMenuItem({
  option,
  isSelected,
  onSelect,
}: {
  option: { start: ReactNode; name: ReactNode }
  isSelected: boolean
  onSelect: ComponentProps<typeof DropdownMenuItemType>['onSelect']
}) {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className="flex min-w-0 items-center justify-between gap-3"
    >
      <span className="flex min-w-0 items-center gap-2">
        {option.start}
        <span className="truncate">{option.name}</span>
      </span>
      {isSelected ? <Check className="size-4 shrink-0" /> : null}
    </DropdownMenuItem>
  )
}

export function ProductVisibilityPicker<
  TOnMount extends ProductEditorSyncValidator,
  TOnChange extends ProductEditorSyncValidator,
  TOnChangeAsync extends ProductEditorAsyncValidator,
  TOnBlur extends ProductEditorSyncValidator,
  TOnBlurAsync extends ProductEditorAsyncValidator,
  TOnSubmit extends ProductEditorSyncValidator,
  TOnSubmitAsync extends ProductEditorAsyncValidator,
  TOnDynamic extends ProductEditorSyncValidator,
  TOnDynamicAsync extends ProductEditorAsyncValidator,
  TOnServer extends ProductEditorAsyncValidator,
  TSubmitMeta,
>({
  className = '',
  form,
  id,
  productRole,
  label = 'Visible on website',
}: {
  className?: string
  form: ProductEditorForm<
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >
  id: string
  productRole: ProductRole
  label?: ReactNode
}) {
  const { isReadOnly } = useIsReadOnly()
  const storefrontVisibility = useStore(
    form.store,
    (state) =>
      state.values.storefront_visibility ?? ProductStorefrontVisibility.PublishedCollection.Value
  )
  const addOnStorefrontVisibility = useStore(
    form.store,
    (state) =>
      state.values.add_on_storefront_visibility ?? ProductAddOnStorefrontVisibility.Hidden.Value
  )
  const showProductVisibility = productRole !== 'add_on_only'
  const showAddOnVisibility = isAddOnProductRole(productRole)
  const buttonLabel =
    showProductVisibility && showAddOnVisibility
      ? `Product: ${getCombinedVisibilityLabel(
          storefrontVisibilityOptions,
          storefrontVisibility
        )} / Add-on: ${getCombinedVisibilityLabel(
          addOnStorefrontVisibilityOptions,
          addOnStorefrontVisibility
        )}`
      : showAddOnVisibility
        ? getOptionLabel(addOnStorefrontVisibilityOptions, addOnStorefrontVisibility)
        : getOptionLabel(storefrontVisibilityOptions, storefrontVisibility)

  return (
    <div className={['space-y-2', className].filter(Boolean).join(' ')}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className="h-auto min-h-10 w-full min-w-0 justify-between gap-2 py-2 whitespace-normal"
            disabled={isReadOnly}
          >
            <span className="min-w-0 text-left leading-snug break-words">{buttonLabel}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="xl">
          {showProductVisibility ? (
            <>
              <DropdownMenuLabel>Product</DropdownMenuLabel>
              {storefrontVisibilityOptions.map((option) => (
                <ProductVisibilityMenuItem
                  key={`product-${option.id}`}
                  option={option}
                  isSelected={storefrontVisibility === option.id}
                  onSelect={() => {
                    if (!isReadOnly) {
                      form.setFieldValue('storefront_visibility', option.id)
                    }
                  }}
                />
              ))}
            </>
          ) : null}
          {showProductVisibility && showAddOnVisibility ? <DropdownMenuSeparator /> : null}
          {showAddOnVisibility ? (
            <>
              <DropdownMenuLabel>Add-on</DropdownMenuLabel>
              {addOnStorefrontVisibilityOptions.map((option) => (
                <ProductVisibilityMenuItem
                  key={`add-on-${option.id}`}
                  option={option}
                  isSelected={addOnStorefrontVisibility === option.id}
                  onSelect={() => {
                    if (!isReadOnly) {
                      form.setFieldValue('add_on_storefront_visibility', option.id)
                    }
                  }}
                />
              ))}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
